import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getDashboard } from '../../utils/api';
import { formatDate, ORDER_STATUS_COLORS } from '../../utils/helpers';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { FiShoppingBag, FiUsers, FiPackage, FiDollarSign, FiClock, FiTrendingUp } from 'react-icons/fi';
import styles from './AdminDashboard.module.css';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const PIE_COLORS = { pending:'#f59e0b', confirmed:'#2563eb', processing:'#7c3aed', shipped:'#06b6d4', delivered:'#16a34a', cancelled:'#ef4444', returned:'#9ca3af' };

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { getDashboard().then(r => setData(r.data)).finally(() => setLoading(false)); }, []);

  if (loading) return <div className="spinner-center"><div className="spinner"/></div>;
  if (!data) return <div style={{ padding:'2rem', color:'var(--text-muted)' }}>Failed to load dashboard</div>;

  const { stats, monthlyRevenue=[], topProducts=[], recentOrders=[], orderStatus=[] } = data;

  const chartData = monthlyRevenue.map(m => ({ name: MONTHS[m._id.month-1], revenue: m.revenue, orders: m.orders }));
  const pieData   = orderStatus.map(s => ({ name: s._id, value: s.count }));

  const statCards = [
    { title:'Total Revenue', value:`৳${stats.totalRevenue?.toLocaleString()||0}`, icon:FiDollarSign, bg:'#e6f4f0', color:'#006A4E' },
    { title:'Total Orders',  value:stats.totalOrders, icon:FiShoppingBag, bg:'#dbeafe', color:'#2563eb' },
    { title:'Total Users',   value:stats.totalUsers,  icon:FiUsers,       bg:'#ede9fe', color:'#7c3aed' },
    { title:'Products',      value:stats.totalProducts,icon:FiPackage,    bg:'#fef3c7', color:'#d97706' },
  ];

  return (
    <div className={styles.page}>
      {/* Stats */}
      <div className={styles.statGrid}>
        {statCards.map(c => (
          <div key={c.title} className={styles.statCard}>
            <div className={styles.statIcon} style={{ background:c.bg, color:c.color }}><c.icon size={22}/></div>
            <div>
              <p className={styles.statLabel}>{c.title}</p>
              <p className={styles.statValue}>{typeof c.value === 'number' ? c.value.toLocaleString() : c.value}</p>
            </div>
            {c.title==='Total Orders' && stats.pendingOrders > 0 && (
              <span className="badge badge-warning" style={{ marginLeft:'auto' }}>{stats.pendingOrders} pending</span>
            )}
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className={styles.chartGrid}>
        <div className={styles.chartCard}>
          <div className={styles.chartHeader}><h3><FiTrendingUp size={15}/> Revenue (6 months)</h3></div>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0"/>
                <XAxis dataKey="name" tick={{ fontSize:11 }}/>
                <YAxis tick={{ fontSize:11 }} tickFormatter={v => `৳${(v/1000).toFixed(0)}k`}/>
                <Tooltip formatter={v => `৳${Number(v).toLocaleString()}`}/>
                <Line type="monotone" dataKey="revenue" stroke="#006A4E" strokeWidth={2.5} dot={{ fill:'#006A4E', r:3 }}/>
              </LineChart>
            </ResponsiveContainer>
          ) : <p style={{ padding:'2rem', textAlign:'center', color:'var(--text-muted)', fontSize:'.875rem' }}>No revenue data yet</p>}
        </div>

        <div className={styles.chartCard}>
          <div className={styles.chartHeader}><h3>📊 Order Status</h3></div>
          {pieData.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" innerRadius={55} outerRadius={85} dataKey="value" label={({ name, value }) => `${name}: ${value}`} labelLine={false} fontSize={10}>
                  {pieData.map((entry, i) => <Cell key={i} fill={PIE_COLORS[entry.name] || '#9ca3af'}/>)}
                </Pie>
                <Tooltip/>
              </PieChart>
            </ResponsiveContainer>
          ) : <p style={{ padding:'2rem', textAlign:'center', color:'var(--text-muted)', fontSize:'.875rem' }}>No orders yet</p>}
        </div>

        <div className={styles.chartCard}>
          <div className={styles.chartHeader}><h3>📦 Orders / Month</h3></div>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0"/>
                <XAxis dataKey="name" tick={{ fontSize:11 }}/>
                <YAxis tick={{ fontSize:11 }}/>
                <Tooltip/>
                <Bar dataKey="orders" fill="#E8192C" radius={[4,4,0,0]}/>
              </BarChart>
            </ResponsiveContainer>
          ) : <p style={{ padding:'2rem', textAlign:'center', color:'var(--text-muted)', fontSize:'.875rem' }}>No data yet</p>}
        </div>

        <div className={styles.chartCard}>
          <div className={styles.chartHeader}>
            <h3>🔥 Top Products</h3>
            <Link to="/admin/products" style={{ fontSize:'.78rem', color:'var(--primary)' }}>View All</Link>
          </div>
          {topProducts.length === 0
            ? <p style={{ padding:'2rem', textAlign:'center', color:'var(--text-muted)', fontSize:'.875rem' }}>No data yet</p>
            : topProducts.map((p,i) => (
            <div key={p._id} className={styles.topItem}>
              <span className={styles.rank}>#{i+1}</span>
              <img src={p.thumbnail} alt={p.name} className={styles.topImg}/>
              <div className={styles.topInfo}>
                <p>{p.name}</p>
                <p>৳{p.price?.toLocaleString()}</p>
              </div>
              <span className={styles.soldBadge}>{p.soldCount} sold</span>
            </div>
          ))}
        </div>
      </div>

      {/* Recent orders */}
      <div className={styles.recentCard}>
        <div className={styles.chartHeader}>
          <h3><FiClock size={14}/> Recent Orders</h3>
          <Link to="/admin/orders" style={{ fontSize:'.78rem', color:'var(--primary)' }}>View All</Link>
        </div>
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead><tr><th>Order #</th><th>Customer</th><th>Items</th><th>Total</th><th>Status</th><th>Date</th></tr></thead>
            <tbody>
              {recentOrders.length === 0
                ? <tr><td colSpan={6} style={{ textAlign:'center', padding:'2rem', color:'var(--text-muted)', fontSize:'.875rem' }}>No orders yet</td></tr>
                : recentOrders.map(o => {
                const sc = ORDER_STATUS_COLORS[o.status] || {};
                return (
                  <tr key={o._id}>
                    <td><span style={{ fontWeight:700, color:'var(--primary)', fontSize:'.875rem' }}>#{o.orderNumber}</span></td>
                    <td style={{ fontSize:'.82rem' }}>{o.user?.name || 'Guest'}</td>
                    <td style={{ fontSize:'.82rem' }}>{o.items?.length} item(s)</td>
                    <td><strong style={{ fontSize:'.875rem' }}>৳{o.totalPrice?.toLocaleString()}</strong></td>
                    <td><span className="badge" style={{ background:sc.bg, color:sc.color }}>{sc.label||o.status}</span></td>
                    <td style={{ color:'var(--text-muted)', fontSize:'.76rem' }}>{formatDate(o.createdAt)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
